import type { ContractItemDetailResponse } from './types';

import {
  type DefaultError,
  type QueryFunctionContext,
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysContract } from '../queryKeys';

import { fetchContractItem } from './fetchContractItem';

/**
 * Generates the query options for the single contract item query so they can
 * be shared between the useQuery hook and loaders/prefetching.
 *
 * @param contractId - The UUID of the contract the item belongs to.
 * @param itemId - The UUID of the item to fetch.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getContractItemQueryOptions = <TData = ContractItemDetailResponse>(
  contractId: UUID | undefined,
  itemId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<ContractItemDetailResponse, DefaultError, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  queryOptions({
    queryFn:
      contractId && itemId
        ? async ({ signal }: QueryFunctionContext) =>
            fetchContractItem(contractId, itemId, { signal })
        : skipToken,
    queryKey: queryKeysContract.getItem(contractId, itemId),
    staleTime: ms('30s'),
    ...options,
  });

/**
 * React Query based custom hook for getting a single contract line item.
 * Consumers decide how a failure surfaces; a 404 means the item does not
 * exist (e.g. deleted, or a stale deep link).
 *
 * @param contractId - The UUID of the contract the item belongs to.
 * @param itemId - The UUID of the item to fetch.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/Contracts/operation/get_contract_item_contracts__contract_id__items__item_id__get
 */
export const useQueryContractItem = <TData = ContractItemDetailResponse>(
  contractId: UUID | undefined,
  itemId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<ContractItemDetailResponse, DefaultError, TData>,
    'queryKey' | 'queryFn'
  >,
) => useQuery(getContractItemQueryOptions(contractId, itemId, options));
